import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import {
  CATALOG_IMPORT_PAGE_SIZE,
  CatalogBulkActionResult,
  CatalogCropResult,
  CatalogDraft,
  CatalogDraftBulkActionRq,
  CatalogDraftListQuery,
  CatalogDraftPage,
  CatalogImport,
  CatalogImportConfig,
  CatalogImportListQuery,
  CatalogPage,
  CatalogSectionMapRq,
  CreateCatalogCropRq,
  CreateCatalogImportResult,
  CreateCatalogImportRq,
  MergeCatalogDraftsRq,
  RerunCatalogPageRq,
  SplitCatalogDraftRq,
  UpdateCatalogDraftRq,
} from "../../interfaces/CatalogImportObject";
import { getRequest, patchRequest, postRequest, putRequest } from "../http/httpRequest";
import { ApiEnvelopeError, unwrapEnvelope } from "../helpers/ApiEnvelopeHelper";
import {
  CATALOG_IMPORT_CLIENT_ERROR,
  CATALOG_IMPORT_ERROR,
  CATALOG_IMPORT_GENERIC_ERROR,
  catalogImportErrorMessage,
  isRetryableCatalogImportCode,
  retryDelay,
} from "../helpers/CatalogImportErrors";
import {
  buildCommitPayload,
  buildCreateImportPayload,
  buildRerunPayload,
  buildSectionMapsPayload,
  buildUpdateDraftPayload,
} from "../converters/uiToBackend/CatalogImportConvertor";
import {
  convertBulkResult,
  convertConfig,
  convertCreateResult,
  convertCropResult,
  convertDraft,
  convertDraftPage,
  convertDraftsChange,
  convertImport,
  convertImportList,
  convertPage,
} from "../converters/backendToUi/catalogImportConvertor";

/**
 * Client for the catalogue PDF import — `/api/v1/catalog-imports`. Contract:
 * interfaces/CatalogImportObject.ts (mirrors json-lib-api's catalogimport
 * models) and product/docs/catalog-import-design.md §9.
 *
 * Like AI batch edits, these return their REAL HTTP status on failure
 * (400/403/404/409/413/415/422/429/502/503) with the NineBusinessRs envelope as
 * the body, so unwrapEnvelope parses the error and we rethrow it as a
 * CatalogImportApiError carrying the code (16201-16299), the HTTP status and
 * `additionalDetails` (e.g. `existingImportId` on DUPLICATE_IMPORT).
 *
 * Retry rule: network failures and 5xx are retried with backoff for GETs ONLY.
 * POST / PATCH / PUT are never repeated automatically — a replayed create would
 * make a second import, a replayed PATCH can overwrite a newer edit.
 */
const BEARER = true;

/** A failed catalogue import call: the code, the HTTP status and whether the same call may work later. */
export class CatalogImportApiError extends ApiEnvelopeError {
  httpStatus: number;
  retryable: boolean;

  constructor(
    errorCode: string,
    message: string,
    httpStatus: number,
    additionalDetails?: Record<string, unknown>
  ) {
    super(errorCode, message, additionalDetails);
    this.name = "CatalogImportApiError";
    this.httpStatus = httpStatus;
    const flagged = additionalDetails?.retryable;
    this.retryable =
      typeof flagged === "boolean"
        ? flagged
        : isRetryableCatalogImportCode(errorCode) || httpStatus >= 502 || httpStatus === 0;
  }

  /** DUPLICATE_IMPORT / IMPORT_ALREADY_RUNNING: the import that is in the way. */
  get existingImportId(): string | null {
    const id = this.additionalDetails?.existingImportId ?? this.additionalDetails?.importId;
    return typeof id === "string" && id !== "" ? id : null;
  }
}

const isCatalogCode = (code: string): boolean => /^162\d\d$/.test(code);

/** Network failures, gateway errors and 5xx — the only failures a GET is retried for. */
function isTransient(error: unknown): boolean {
  return (
    error instanceof CatalogImportApiError &&
    (error.errorCode === CATALOG_IMPORT_CLIENT_ERROR.NETWORK || error.httpStatus >= 500)
  );
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const MAX_GET_RETRIES = 2;

interface SendOptions {
  /** GETs only. */
  retry?: boolean;
}

/** One request -> converted payload, with the error mapping and (for GETs) retry described above. */
async function send<T>(
  make: () => Promise<Response>,
  convert: (contents: unknown) => T,
  { retry = false }: SendOptions = {}
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      let response: Response;
      try {
        response = await make();
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") throw error;
        throw new CatalogImportApiError(
          CATALOG_IMPORT_CLIENT_ERROR.NETWORK,
          catalogImportErrorMessage(CATALOG_IMPORT_CLIENT_ERROR.NETWORK, CATALOG_IMPORT_GENERIC_ERROR),
          0
        );
      }
      const status = response.status;
      try {
        return convert(await unwrapEnvelope<unknown>(response));
      } catch (error) {
        if (error instanceof ApiEnvelopeError && !(error instanceof CatalogImportApiError)) {
          throw new CatalogImportApiError(error.errorCode, error.message, status, error.additionalDetails);
        }
        throw error;
      }
    } catch (error) {
      if (retry && attempt < MAX_GET_RETRIES && isTransient(error)) {
        await sleep(retryDelay(attempt));
        continue;
      }
      throw error;
    }
  }
}

/**
 * Converts any error thrown by this service into curator-facing text. Request
 * errors use the server's message (curator-safe by contract, and it carries
 * specifics like page counts); gateway noise and unknown codes get our copy.
 */
export function toFriendlyCatalogImportError(error: unknown): string {
  if (error instanceof ApiEnvelopeError) {
    if (isCatalogCode(error.errorCode)) {
      const server = error.message?.trim();
      return server || catalogImportErrorMessage(error.errorCode, CATALOG_IMPORT_GENERIC_ERROR);
    }
    if (error.errorCode === CATALOG_IMPORT_CLIENT_ERROR.NETWORK) {
      return catalogImportErrorMessage(error.errorCode, CATALOG_IMPORT_GENERIC_ERROR);
    }
    if (/^HTTP_5\d\d$/.test(error.errorCode)) {
      return "The service is temporarily unavailable. Please try again in a moment.";
    }
    return error.message?.trim() || CATALOG_IMPORT_GENERIC_ERROR;
  }
  if (error instanceof DOMException && error.name === "AbortError") {
    return catalogImportErrorMessage(CATALOG_IMPORT_CLIENT_ERROR.ABORTED, "Cancelled.");
  }
  return error instanceof Error && error.message ? error.message : CATALOG_IMPORT_GENERIC_ERROR;
}

/** True when this error means the import / page / draft no longer exists or is not the caller's. */
export function isCatalogImportAccessError(error: unknown): boolean {
  return (
    error instanceof CatalogImportApiError &&
    (error.errorCode === CATALOG_IMPORT_ERROR.IMPORT_NOT_FOUND ||
      error.errorCode === CATALOG_IMPORT_ERROR.NOT_IMPORT_OWNER)
  );
}

// ── Paths ────────────────────────────────────────────────────────────────────

const BASE = ServiceEndpoints.CATALOG_IMPORTS;
const enc = encodeURIComponent;

const importPath = (importId: string, suffix = ""): string => `${BASE}/${enc(importId)}${suffix}`;

// ── Import lifecycle ─────────────────────────────────────────────────────────

/** GET /config — limits and the caller's remaining pages today. */
function getConfig(): Promise<CatalogImportConfig> {
  return send(() => getRequest(`${BASE}/config`, BEARER), convertConfig, { retry: true });
}

/** POST / — creates the import (AWAITING_UPLOAD) and returns the signed upload target. */
function createImport(form: CreateCatalogImportRq): Promise<CreateCatalogImportResult> {
  return send(
    () => postRequest(BASE, buildCreateImportPayload(form), BEARER),
    convertCreateResult
  );
}

/** POST /{id}/upload-url — a fresh signed URL while AWAITING_UPLOAD (retry / resume / expired). */
function requestUploadUrl(importId: string): Promise<CreateCatalogImportResult> {
  return send(() => postRequest(importPath(importId, "/upload-url"), {}, BEARER), convertCreateResult);
}

/** POST /{id}/start — after the PDF is uploaded; the server checks the object, then QUEUED. */
function startImport(importId: string): Promise<CatalogImport> {
  return send(() => postRequest(importPath(importId, "/start"), {}, BEARER), convertImport);
}

/** POST /{id}/cancel — allowed up to CONSOLIDATING. */
function cancelImport(importId: string): Promise<CatalogImport> {
  return send(() => postRequest(importPath(importId, "/cancel"), {}, BEARER), convertImport);
}

/** POST /{id}/retry-failed-pages — re-queues FAILED pages (re-render + extract). */
function retryFailedPages(importId: string): Promise<CatalogImport> {
  return send(() => postRequest(importPath(importId, "/retry-failed-pages"), {}, BEARER), convertImport);
}

/** POST /{id}/archive — rejects what is left and archives. */
function archiveImport(importId: string): Promise<CatalogImport> {
  return send(() => postRequest(importPath(importId, "/archive"), {}, BEARER), convertImport);
}

/**
 * GET /?status&businessId&before&limit — newest first. `before` is the previous
 * page's last `createdAt`. The scope (own imports vs all) comes from the token.
 */
function listImports({ filter, businessId, before }: CatalogImportListQuery): Promise<CatalogImport[]> {
  const params = new URLSearchParams({ status: filter, limit: String(CATALOG_IMPORT_PAGE_SIZE) });
  if (businessId) params.set("businessId", businessId);
  if (before) params.set("before", before);
  return send(() => getRequest(`${BASE}?${params.toString()}`, BEARER), convertImportList, { retry: true });
}

/** GET /{id} — the import with progress counters, section maps and page summaries. */
function getImport(importId: string): Promise<CatalogImport> {
  return send(() => getRequest(importPath(importId), BEARER), convertImport, { retry: true });
}

// ── Review ───────────────────────────────────────────────────────────────────

/** GET /{id}/pages/{pageNo} — signed image URLs, regions, crops and the page's drafts. */
function getPage(importId: string, pageNo: number): Promise<CatalogPage> {
  return send(() => getRequest(importPath(importId, `/pages/${pageNo}`), BEARER), convertPage, {
    retry: true,
  });
}

/** GET /{id}/drafts?status&flag&pageNo&cursor. */
function getDrafts({ importId, status, flag, pageNo, cursor }: CatalogDraftListQuery): Promise<CatalogDraftPage> {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (flag) params.set("flag", flag);
  if (pageNo !== undefined) params.set("pageNo", String(pageNo));
  if (cursor) params.set("cursor", cursor);
  const query = params.toString();
  return send(
    () => getRequest(importPath(importId, `/drafts${query ? `?${query}` : ""}`), BEARER),
    convertDraftPage,
    { retry: true }
  );
}

/** PATCH /{id}/drafts/{draftId} — optimistic `version`; a stale one is DRAFT_VERSION_CONFLICT (16222). */
function patchDraft(importId: string, draftId: string, form: UpdateCatalogDraftRq): Promise<CatalogDraft> {
  return send(
    () => patchRequest(importPath(importId, `/drafts/${enc(draftId)}`), buildUpdateDraftPayload(form), BEARER),
    convertDraft
  );
}

/** POST /{id}/drafts/actions — approve / reject / restore / unapprove by ids, page or all-unflagged. */
function bulkDraftAction(importId: string, form: CatalogDraftBulkActionRq): Promise<CatalogBulkActionResult> {
  return send(() => postRequest(importPath(importId, "/drafts/actions"), form, BEARER), convertBulkResult);
}

/** POST /{id}/drafts/merge — at least two drafts become one product with variants. */
function mergeDrafts(importId: string, form: MergeCatalogDraftsRq): Promise<CatalogDraft[]> {
  return send(() => postRequest(importPath(importId, "/drafts/merge"), form, BEARER), convertDraftsChange);
}

/** POST /{id}/drafts/{draftId}/split — variants become products of their own. */
function splitDraft(importId: string, draftId: string, form: SplitCatalogDraftRq): Promise<CatalogDraft[]> {
  return send(
    () => postRequest(importPath(importId, `/drafts/${enc(draftId)}/split`), form, BEARER),
    convertDraftsChange
  );
}

/** POST /{id}/pages/{pageNo}/crops — a manual crop, optionally attached to / creating a draft. */
function createCrop(importId: string, pageNo: number, form: CreateCatalogCropRq): Promise<CatalogCropResult> {
  return send(
    () => postRequest(importPath(importId, `/pages/${pageNo}/crops`), form, BEARER),
    convertCropResult
  );
}

/** POST /{id}/pages/{pageNo}/rerun — sync re-extraction; returns the refreshed page. */
function rerunPage(importId: string, pageNo: number, form: RerunCatalogPageRq): Promise<CatalogPage> {
  return send(
    () => postRequest(importPath(importId, `/pages/${pageNo}/rerun`), buildRerunPayload(form), BEARER),
    convertPage
  );
}

/** PUT /{id}/section-maps — maps PDF series to business sections (or creates sections). */
function putSectionMaps(importId: string, maps: CatalogSectionMapRq[]): Promise<CatalogImport> {
  return send(
    () => putRequest(importPath(importId, "/section-maps"), buildSectionMapsPayload(maps), BEARER),
    convertImport
  );
}

/** POST /{id}/commit — creates the approved drafts (all, or only `draftIds`). */
function commitImport(importId: string, draftIds?: string[]): Promise<CatalogImport> {
  return send(
    () => postRequest(importPath(importId, "/commit"), buildCommitPayload(draftIds), BEARER),
    convertImport
  );
}

// ── Direct upload to the signed URL ──────────────────────────────────────────

export interface UploadProgressEvent {
  loaded: number;
  total: number;
}

/** Why a PUT to the signed URL failed. `expired` = the signature is no longer valid (ask for a new URL). */
export type PdfUploadFailureKind = "network" | "expired" | "rejected" | "aborted";

export class PdfUploadError extends Error {
  kind: PdfUploadFailureKind;
  httpStatus: number;

  constructor(kind: PdfUploadFailureKind, httpStatus: number) {
    super(
      kind === "expired"
        ? catalogImportErrorMessage(CATALOG_IMPORT_CLIENT_ERROR.UPLOAD_EXPIRED, CATALOG_IMPORT_GENERIC_ERROR)
        : kind === "network"
          ? catalogImportErrorMessage(CATALOG_IMPORT_CLIENT_ERROR.NETWORK, CATALOG_IMPORT_GENERIC_ERROR)
          : kind === "aborted"
            ? catalogImportErrorMessage(CATALOG_IMPORT_CLIENT_ERROR.ABORTED, "Cancelled.")
            : catalogImportErrorMessage(CATALOG_IMPORT_CLIENT_ERROR.UPLOAD_REJECTED, CATALOG_IMPORT_GENERIC_ERROR)
    );
    this.name = "PdfUploadError";
    this.kind = kind;
    this.httpStatus = httpStatus;
  }
}

/**
 * PUTs the PDF straight to the signed storage URL (no apiDomain, no bearer
 * token — the URL carries its own authorisation). XHR rather than fetch
 * because only XHR reports upload progress. Resolves on 2xx; rejects with a
 * PdfUploadError. `signal` aborts the request.
 */
function uploadPdfToSignedUrl(
  file: Blob,
  uploadUrl: string,
  onProgress: (event: UploadProgressEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new PdfUploadError("aborted", 0));
      return;
    }
    const xhr = new XMLHttpRequest();
    const cleanup = () => signal?.removeEventListener("abort", onAbort);
    const onAbort = () => {
      xhr.abort();
    };
    signal?.addEventListener("abort", onAbort);

    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", "application/pdf");
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress({ loaded: event.loaded, total: event.total });
    };
    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress({ loaded: file.size, total: file.size });
        resolve();
      } else if (xhr.status === 401 || xhr.status === 403) {
        // A signed PUT URL answers 403 once its signature has expired.
        reject(new PdfUploadError("expired", xhr.status));
      } else if (xhr.status >= 500 || xhr.status === 0) {
        reject(new PdfUploadError("network", xhr.status));
      } else {
        reject(new PdfUploadError("rejected", xhr.status));
      }
    };
    xhr.onerror = () => {
      cleanup();
      reject(new PdfUploadError("network", 0));
    };
    xhr.ontimeout = () => {
      cleanup();
      reject(new PdfUploadError("network", 0));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new PdfUploadError("aborted", 0));
    };
    xhr.send(file);
  });
}

const CatalogImportApiService = {
  getConfig,
  createImport,
  requestUploadUrl,
  startImport,
  cancelImport,
  retryFailedPages,
  archiveImport,
  listImports,
  getImport,
  getPage,
  getDrafts,
  patchDraft,
  bulkDraftAction,
  mergeDrafts,
  splitDraft,
  createCrop,
  rerunPage,
  putSectionMaps,
  commitImport,
  uploadPdfToSignedUrl,
};

export default CatalogImportApiService;
